import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react'
import { Row, Col } from 'react-bootstrap'
import ReactBootstrapSlider from 'react-bootstrap-slider'
import moment from 'moment'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { FaChevronLeft, FaChevronRight, FaPlay, FaPause } from 'react-icons/fa'
import momenttz from '../../../component/helpers/momenttz'
import useInterval from '../../../component/common/UseInterval'

import './TripReplayControls.sass'
import 'bootstrap-slider/dist/css/bootstrap-slider.css'

const TripReplayControls = forwardRef((props, ref) => {
	const dispatch = useDispatch();
	const { onClickPlayback, onTripEnd, autoplayEnabled, setAutoplayEnabled } = props;
	const { t } = useTranslation('translation', { keyPrefix: 'homePage.vehicleSidebar.tripsGrid.tripReplayControls' });
	const { tripsGPS, selectedTrip, tripTimelineTimestamp } = useSelector(state => state.dashboardData);
	const [intervalPlayback, setIntervalPlayback] = useState(null)
	const [intervalLongPress, setIntervalLongPress] = useState(null)
	const [initPlaybackSpeed, setInitPlaybackSpeed] = useState(1)
	const [playbackSpeed, setPlaybackSpeed] = useState(1)
	const [currentTimelineDate, setCurrentTimelineDate] = useState(null)
	const [isPlaying, setIsPlaying] = useState(false)
	const [playbackDuration, setPlaybackDuration] = useState(1) // selected speed of playback (30sec, 45sec, Real Time)

	const [sliderValManually, setSliderValManually] = useState(0)
	const [delay, setDelay] = useState(null)
	const [speed, setSpeed] = useState(0);

	const setTripTimelineTimestamp = useCallback((state) => {
		dispatch({ type: 'SET_TRIP_TIMELINE_TIMESTAMP', payload: state })
	}, [dispatch]);

	const setTripPlaybackTimestamp = useCallback((state) => {
		dispatch({ type: 'SET_TRIP_PLAYBACK_TIMESTAMP', payload: state })
	}, [dispatch]);

	useEffect(() => {
		if (sliderValManually > 0) {
			const val = sliderValManually;

			const currentTimelineDate = momenttz(selectedTrip.start_gps.timestamp + '+00').add(val, 'seconds')
			setCurrentTimelineDate(currentTimelineDate)
			setTripTimelineTimestamp(currentTimelineDate.format('YYYY-MM-DD HH:mm:ss'))
		}
	}, [sliderValManually])

	useEffect(() => {
		if (tripsGPS.length) {
			setCurrentTimelineDate(tripsGPS[0].polygon.length ? moment(tripsGPS[0].polygon[0].timestamp) : null)
			setIsPlaying(false)
			setDelay(null)

			if (intervalPlayback) {
				clearInterval(intervalPlayback)
				setIntervalPlayback(null)
			}
		}

	}, [tripsGPS])

	useEffect(() => {
		if (selectedTrip && selectedTrip.duration > 45) {
			let playbackSpeed = (selectedTrip.duration / 45).toFixed(1);
			setPlaybackSpeed(playbackSpeed);
			setInitPlaybackSpeed(playbackSpeed);
			setPlaybackDuration(1)

			if (intervalPlayback) {
				clearInterval(intervalPlayback)
				setIntervalPlayback(null)
			}
		}
	}, [selectedTrip])

	useEffect(() => {
		if (tripTimelineTimestamp) {
			setCurrentTimelineDate(moment(tripTimelineTimestamp))
			setIsPlaying(false)
			setDelay(null)

			if (intervalPlayback) {
				clearInterval(intervalPlayback)
				setIntervalPlayback(null)
			}
		}
	}, [tripTimelineTimestamp])

	useInterval(() => {
		if (!tripsGPS.length || !tripsGPS[0].polygon || tripsGPS[0].polygon.length === 0) {
			return;
		}

		const currentPlaybackTime = moment(currentTimelineDate).add((1 * Math.abs(speed) / 10), 'seconds')
		const pos = _.find(tripsGPS[0].polygon, (item) => {
			return moment(item.timestamp) >= currentPlaybackTime
		})
		if (pos) {
			setCurrentTimelineDate(currentPlaybackTime)
			onClickPlayback(currentPlaybackTime)
			setTripPlaybackTimestamp(currentPlaybackTime.format('YYYY-MM-DD HH:mm:ss'))
		} else {
			setDelay(null)
			setIsPlaying(false)
			if (onTripEnd && autoplayEnabled) {
				onTripEnd(selectedTrip)
			}
		}
	}, delay);

	const playStopPlayback = (e, playback_speed, argDelay) => {
		const varDelay = argDelay === undefined ? delay : argDelay

		if (varDelay) {
			setDelay(null)
			setIsPlaying(false)
		}
		else {
			let speedNew = playbackSpeed
			if (playback_speed) {
				speedNew = playback_speed
			}
			let add_sec = 1
			if (speedNew < 0) {
				add_sec = -1
			}

			setSpeed(speedNew)
			setDelay(100)
			setIsPlaying(true)
			setAutoplayEnabled(true);
		}
	}

	const changePlaybackDuration = (event) => {
		let value = event.target && event.target.value;
		let speed = value !== "0" ? value * initPlaybackSpeed : 1;

		if (delay) {
			playStopPlayback(event, speed, null)
		}

		setPlaybackSpeed(speed);
		setPlaybackDuration(value)
	}

	const changeTripControlSlider = (e) => {
		setSliderValManually(e.target.value)
	}

	const clearIntervalLongPress = () => {
		if (intervalLongPress) {
			clearInterval(intervalLongPress);
			setIntervalLongPress(null)
		}
	}

	const handleArrowDown = (direction) => {
		clearIntervalLongPress()
		let timer = 0
		let secs = 1
		setIntervalLongPress(setInterval(() => {
			timer += 1
			if (timer < 20) {
				secs = 5
			} else if (timer < 40) {
				secs = 30
			} else {
				secs = 60
			}

			let shiftTime = null
			if (direction === 'prev') {
				shiftTime = currentTimelineDate.add(-secs, 'seconds')
				if (shiftTime < momenttz(selectedTrip.start_gps.timestamp + '+00')) {
					shiftTime = momenttz(selectedTrip.start_gps.timestamp + '+00')
				}
			} else {
				shiftTime = currentTimelineDate.add(secs, 'seconds')
				if (shiftTime > momenttz(selectedTrip.end_gps.timestamp + '+00')) {
					shiftTime = momenttz(selectedTrip.end_gps.timestamp + '+00')
				}
			}

			setCurrentTimelineDate(shiftTime)
			if (!delay) {
				setTripTimelineTimestamp(shiftTime.format('YYYY-MM-DD HH:mm:ss'))
			}
		}, 100))
	}

	const isDisabledControls = _.isEmpty(selectedTrip)

	let options = [<option key={1.5} value={1.5}>{t('option30s')}</option>];
	if (selectedTrip) {
		if (selectedTrip.duration > 45) {
			options.push(
				<option key={1} value={1}>{t('option45s')}</option>
			)
		}
		if (selectedTrip.duration > 60) {
			options.push(<option key={0.75} value={0.75}>{t('option1m')}</option>)
		}
		if (selectedTrip.duration > 120) {
			options.push(<option key={0.375} value={0.375}>{t('option2m')}</option>)
		}
		options.push(<option key={0} value={0}>{t('optionRealTime')}</option>);
	}

	let sliderValue = 0
	if (currentTimelineDate && !isDisabledControls) {
		sliderValue = currentTimelineDate.diff(momenttz(selectedTrip.start_gps.timestamp + '+00'), 'seconds')
	}

	let sliderMax = 10
	if (!isDisabledControls) {
		sliderMax = selectedTrip.duration
	}

	useImperativeHandle(ref, () => ({
		playStopPlayback
	}));

	return (
		<div className={`tripReplayControls ${isDisabledControls && 'disabled'}`}>
			<div className="header">
				{t('titleTripReplayControls')}
			</div>
			<div>
				<div className="timeWrapper">
					<div>
						<b>{t('titleStart')}</b>
						<div className="timeValue">
							{!isDisabledControls && selectedTrip.start_gps
								? momenttz(`${selectedTrip.start_gps.timestamp}+00`).format('h:mm:ss a')
								: '--:--:--'
							}
						</div>
					</div>
					<div className="currentValueWrapper">
						<FaChevronLeft
							className="arrow prevTime"
							onMouseDown={() => handleArrowDown('prev')}
							onMouseUp={clearIntervalLongPress}
						/>
						<span className="currentValue">
							{currentTimelineDate
								? momenttz(currentTimelineDate).format('h:mm:ss a')
								: '--:--:--'
							}
						</span>
						<FaChevronRight
							className="arrow nextTime"
							onMouseDown={() => handleArrowDown('next')}
							onMouseUp={clearIntervalLongPress}
						/>
					</div>
					<div>
						<b>{(!isDisabledControls && selectedTrip.isLiveTrip) ? t('titleLive') : t('titleEnd') }</b>
						<div className="timeValue">
							{!isDisabledControls && selectedTrip.end_gps
								? momenttz(`${selectedTrip.end_gps.timestamp}+00`).format('h:mm:ss a')
								: '--:--:--'
							}
						</div>
					</div>
				</div>
				<ReactBootstrapSlider
					id="video-slider"
					name="horizontalSliderName"
					value={sliderValue}
					max={sliderMax}
					min={0}
					step={1}
					tooltip="hide"
					change={changeTripControlSlider}
					disabled={isDisabledControls ? 'disabled' : 'enabled'}
				/>
				<Row className="text-center" style={{ margin: 'auto' }}>
					<Col xs={3}>
						{isPlaying
							? <FaPause className="startPlayback" onClick={(e) => isDisabledControls ? null : playStopPlayback(e)} />
							: <FaPlay id="trip_replay_play_click" className="startPlayback" onClick={(e) => isDisabledControls ? null : playStopPlayback(e)} />
						}
					</Col>
					{selectedTrip && selectedTrip.duration > 30 &&
						<Col xs={9} style={{ textAlign: 'right' }}>
							<b style={{ fontSize: '16px' }}>{t('titleReplayDuration')}</b>
							<select
								id="trip-duration-select"
								placeholder={t('option45s')}
								onChange={(e) => changePlaybackDuration(e)}
								value={playbackDuration}
							>
								{options}
							</select>
						</Col>
					}
				</Row>
			</div>
		</div>
	)
})

export default TripReplayControls;
